import { Button, Loader, Stack, Tooltip, VisuallyHidden } from "@mantine/core";
import { Check, RefreshCcw } from "lucide-react";
import { useId, type ReactNode } from "react";

/** The button's four states, in live's order (`generation-status-button`). */
export type StatusStep = "generate" | "generating" | "generated" | "regenerate";

export interface StatusButtonProps {
  step: StatusStep;
  /** Live's words for each state, as the form has them. */
  label: string;
  disabled: boolean;
  /** A start on its way: Mantine's loading look over the button. */
  loading: boolean;
  /** Why it is grey, a line each (live's tooltip); none hides the tooltip. */
  blockers: readonly string[];
  onClick: () => void;
}

const ICONS: Record<StatusStep, ReactNode> = {
  generate: null,
  generating: <Loader size={16} aria-hidden />,
  generated: <Check size={16} aria-hidden />,
  regenerate: <RefreshCcw size={16} aria-hidden />,
};

/**
 * Live's status button of the audio steps (Talking Head, Lip-Sync): filled to generate and while
 * generating, outlined once there is a result; live's icon for each state, and its tooltip of
 * reasons while it is grey for them.
 */
export function StatusButton({ step, label, disabled, loading, blockers, onClick }: StatusButtonProps) {
  const reasons = useId();
  const button = (
    <Button
      aria-describedby={blockers.length === 0 ? undefined : reasons}
      variant={step === "generated" || step === "regenerate" ? "outline" : "filled"}
      loading={loading}
      disabled={disabled}
      leftSection={ICONS[step]}
      onClick={onClick}
    >
      {label}
    </Button>
  );
  if (blockers.length === 0) return button;
  return (
    <Tooltip
      label={
        <Stack gap={0}>
          {blockers.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </Stack>
      }
      withArrow
    >
      {/* A disabled button takes no hover, so the tooltip hangs from a wrapper. */}
      <span>
        {button}
        <VisuallyHidden id={reasons}>{blockers.join(" ")}</VisuallyHidden>
      </span>
    </Tooltip>
  );
}
